/**
 * Product Database Operations
 * 
 * Client-side database operations for products.
 * Products are shared resources (no user scoping needed).
 */

import { collection, query, getDocs, onSnapshot, QuerySnapshot, Timestamp } from 'firebase/firestore';
import { getDb } from '@/lib/firebase-config';
import { Product } from '../types/product';

const PRODUCTS_COLLECTION = 'products';

/**
 * Get all products
 * Products are shared resources, so no user filtering is needed
 */
export async function getProducts(): Promise<Product[]> {
  try {
    const db = getDb();
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    const snapshot = await getDocs(productsRef);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Product[];
  } catch (error) {
    logger.error('Error fetching products:', error);
    throw error;
  }
}
import logger from '@/lib/logging';

/**
 * Subscribe to products collection with real-time updates
 * Products are shared resources, so no user filtering is needed
 */
export function subscribeToProducts(
  onUpdate: (products: Product[]) => void,
  onError: (error: Error) => void
): () => void {
  try {
    const db = getDb();
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    const q = query(productsRef);

    return onSnapshot(
      q,
      (snapshot: QuerySnapshot) => {
        try {
          const products = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as Product[];
          onUpdate(products);
        } catch (error) {
          logger.error('Error processing product snapshot:', error);
          onError(error instanceof Error ? error : new Error(String(error)));
        }
      },
      (error) => {
        logger.error('Error subscribing to products:', error);
        onError(error);
      }
    );
  } catch (error) {
    logger.error('Error setting up product subscription:', error);
    onError(error instanceof Error ? error : new Error(String(error)));
    // Return a no-op unsubscribe function
    return () => {};
  }
}

/**
 * Get product by ID
 */
export async function getProductById(productId: string): Promise<Product | null> {
  try {
    const db = getDb();
    const productsRef = collection(db, PRODUCTS_COLLECTION);
    const snapshot = await getDocs(productsRef);

    const productDoc = snapshot.docs.find((doc) => doc.id === productId);
    if (!productDoc) {
      return null;
    }

    return {
      id: productDoc.id,
      ...productDoc.data(),
    } as Product;
  } catch (error) {
    logger.error('Error fetching product by ID:', error);
    throw error;
  }
}

/**
 * Get product by Stripe product ID
 */
export async function getProductByStripeId(providerProductId: string): Promise<Product | null> {
  try {
    const products = await getProducts();
    return products.find((p) => p.providerProductId === providerProductId) || null;
  } catch (error) {
    logger.error('Error fetching product by Stripe ID:', error);
    throw error;
  }
}

/**
 * Get product by Stripe price ID
 */
export async function getProductByPriceId(providerPriceId: string): Promise<Product | null> {
  try {
    const products = await getProducts();
    return products.find((p) => p.providerPriceId === providerPriceId) || null;
  } catch (error) {
    logger.error('Error fetching product by price ID:', error);
    throw error;
  }
}

